/**
 * HTML Editor for Gmail by cloudHQ
 *
 * Exposes four WAR groups — `javascripts`, `icons`, `stylesheets` and `html` (all
 * globbed) — with no `use_dynamic_url`, but matched only to `cloudhq.net` subdomains and
 * to `mail.google.com`. The resources are not exposed to arbitrary pages, so a fetch
 * from any other origin fails at the network layer and proves nothing.
 *
 * From cloudhq.net the vendor-named `icons/gmail_html.png` resolves at a fixed URL
 * whenever the extension is installed, making it a conclusive per-origin signal.
 *
 * Manifest verified against version 2.0.2.8.
 */

import { probeImage, probeFetch } from '../core/probe';
import type { ExtensionDefinition } from '../core/types';

const ID = 'ioinaaeeacahcmbgfmeaaofhfkijpdeb';

export const htmlEditorForGmail: ExtensionDefinition = {
  id: ID,
  name: 'HTML Editor for Gmail by cloudHQ',
  storeUrl: `https://chromewebstore.google.com/detail/${ID}`,
  users: 40_000,
  requiredOrigin: 'https://www.cloudhq.net',
  notes:
    'WAR (icons/stylesheets/html) scoped to cloudhq.net subdomains and mail.google.com, ' +
    'so only those origins can detect it. No dynamic URL.',
  signals: [
    {
      id: 'war-gmail-html-icon',
      kind: 'web-accessible-resource',
      description:
        'Load icons/gmail_html.png via <img> — a vendor-named icon exposed only to ' +
        'cloudhq.net origins; needs no CORS grant.',
      weight: 1,
      run: () => probeImage(ID, 'icons/gmail_html.png'),
    },
    {
      id: 'war-gmail-templates-css',
      kind: 'web-accessible-resource',
      description:
        'Fetch stylesheets/main_gmail_templates.css — a vendor-named stylesheet, ' +
        'independent of the icon path.',
      weight: 0.5,
      run: () => probeFetch(ID, 'stylesheets/main_gmail_templates.css'),
    },
    {
      id: 'war-wrapper-html',
      kind: 'web-accessible-resource',
      description: 'Fetch html/wrapper.html — the editor wrapper entry point, a third path.',
      weight: 0.5,
      run: () => probeFetch(ID, 'html/wrapper.html'),
    },
  ],
};
